/**
 * Client-side referral attribution:
 *   • Reads `?ref=CODE` from the URL on every top-level navigation
 *   • Stores the (last-touch) code in a `fh_ref` cookie for `cookie_days` days
 *   • Fires a one-time click ping to /api/public/referral-visit per session
 *
 * Kept intentionally dependency-free: this runs inside a useEffect at app root,
 * so it must never crash SSR.
 */
import { REFERRAL_CODE_REGEX } from "./referral-schema";

const COOKIE_NAME = "fh_ref";
const SESSION_SEEN_KEY = "fh_ref_ping";
const DEFAULT_COOKIE_DAYS = 90;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const maxAge = Math.max(1, days) * 86400;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    "SameSite=Lax",
    `Max-Age=${maxAge}`,
  ];
  if (typeof location !== "undefined" && location.protocol === "https:") parts.push("Secure");
  document.cookie = parts.join("; ");
}

export function readReferralCookie(): string | null {
  return readCookie(COOKIE_NAME);
}

export function clearReferralCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function initReferralTracker(opts?: { cookieDays?: number }) {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("ref");
    if (!raw) return;
    const code = raw.trim().toUpperCase();
    if (!REFERRAL_CODE_REGEX.test(code)) return;

    writeCookie(COOKIE_NAME, code, opts?.cookieDays ?? DEFAULT_COOKIE_DAYS);

    // Fire-and-forget: throttle to one visit per code per browser session.
    const seenKey = `${SESSION_SEEN_KEY}:${code}`;
    if (window.sessionStorage.getItem(seenKey)) return;
    window.sessionStorage.setItem(seenKey, "1");

    const sessionId =
      window.sessionStorage.getItem("fh_session_id") ||
      (() => {
        const id =
          (window.crypto?.randomUUID?.() as string | undefined) ??
          `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
        window.sessionStorage.setItem("fh_session_id", id);
        return id;
      })();

    void fetch("/api/public/referral-visit", {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        session_id: sessionId,
        referer: document.referrer || null,
        landing_path: url.pathname,
      }),
    }).catch(() => {});
  } catch {
    /* never break the app */
  }
}
